/* eslint-disable lingui/no-unlocalized-strings */
import { t } from "@lingui/macro";
import { ReactiveResumeParser } from "@reactive-resume/parser";
import { ResumeData } from "@reactive-resume/schema";
import { Button } from "@reactive-resume/ui";
import { cn, colorList, layoutsList, marginList } from "@reactive-resume/utils";
import { FilePlus2, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { axios } from "@/client/libs/axios";
import { useImportResume } from "@/client/services/resume/import";
import { useAudioChatStore } from "@/client/stores/audioChatStore";

import { createResumeIntakeSnapshot } from "./_utils/audioChatSnapshot";
import { buildResumeDataFromIntake } from "./_utils/buildResumeDataFromIntake";
import {
  recordLegacyMessage,
  recordLegacyToolFinished,
  recordLegacyToolStarted,
} from "./_utils/legacyTranscriptBridge";
import { ExtrasChat } from "./extrasChat";
import { TypingText } from "./typingText";

type Message = {
  type: "user" | "ai" | "comp";
  content?: string;
  showIllustration?: boolean;
  showOptions?: boolean;
  comp?: "EXTRAS";
};
type messageType = Message[];
type GenerateResumeChatProps = {
  closeDialogue: () => void;
  scrollToBottom: () => void;
  focusTextarea: () => void;
};

const directAudioResumeGenerationEnabled =
  import.meta.env.VITE_DIRECT_AUDIO_RESUME_GENERATION === "true";

const isBadRequestError = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "response" in error &&
  (error as { response?: { status?: number } }).response?.status === 400;

export const GenerateResumeChat = ({
  closeDialogue,
  scrollToBottom,
  focusTextarea,
}: GenerateResumeChatProps) => {
  const [showGenerate, setShowGenerate] = useState(false);
  const [messages, setMessages] = useState<messageType>([]);
  const step = useAudioChatStore((state) => state.step);
  const personalInfo = useAudioChatStore((State) => State.personalInfo);
  const jobAnalysis = useAudioChatStore((state) => state.jobAnalysis);
  const workExpInfo = useAudioChatStore((state) => state.workExpInfo);
  const jobDescription = useAudioChatStore((state) => state.jobDescription);
  const selectedSkills = useAudioChatStore((state) => state.selectedSkills);
  const educationInfo = useAudioChatStore((state) => state.educationInfo);
  const userSummary = useAudioChatStore((state) => state.userSummary);
  const awardsInfo = useAudioChatStore((state) => state.awardsInfo);
  const certificationInfo = useAudioChatStore((state) => state.certificationInfo);
  const languagesInfo = useAudioChatStore((state) => state.languagesInfo);
  const hobbiesInfo = useAudioChatStore((state) => state.hobbiesInfo);
  const currentTemplate = useAudioChatStore((state) => state.currentTemplate);
  const setIsLoading = useAudioChatStore((state) => state.setIsLoading);
  const { importResume } = useImportResume();
  //---

  const pushMessages = useCallback((mes: Message) => {
    setMessages((prevMessages) => [...prevMessages, mes]);
    recordLegacyMessage(mes);
  }, []);

  const generateBlob = () => {
    // const resumeContent = `
    // **${personalInfo.name}**

    // **Phone Number:** ${personalInfo.phone}
    // **Email:** ${personalInfo.email}
    // **LinkedIn:** ${personalInfo.linkedin}

    // **Objective:** ${jobDescription}

    // **Professional Summary:** ${userSummary}

    // **Technical Skills:**
    // ${selectedSkills.map(skill => `* ${skill}`).join('\n')}

    // **Work Experience:**
    // ${workExpInfo.map(exp => `
    // * **${exp.jobCompanyName}**, ${exp.jobtitleAndResp}
    //   + ${exp.jobUniqueImpact}
    // `).join('\n')}

    // **Education:**
    // ${educationInfo.map(edu => `
    //   * **${edu.degree}**, ${edu.institute}
    //     + ${edu.major}
    //   `).join('\n')}

    // **Achievements:**
    // ${awardsInfo.map(award => `
    //   * **${award}**
    //   `).join('\n')}

    // **Certifications:**
    // ${certificationInfo.map(certi => `
    //   * **${certi}**
    //   `).join('\n')}

    // **languages:**
    // ${languagesInfo.map(language => `
    //   * **${language}**
    //   `).join('\n')}

    // **hobbies:**
    // ${hobbiesInfo.map(hobbie => `
    //   * **${hobbie}**
    //   `).join('\n')}
    // `;

    const resumeContent = `
    ${personalInfo.name}
    ${personalInfo.phone}  
    ${personalInfo.email}  
    ${personalInfo.linkedin}
    
    Objective: ${jobDescription}
    
    Summary: ${userSummary}
    
    Technical Skills:
    ${selectedSkills.map((skill) => `* ${skill}`).join(`\n   `)}
    
    Work Experience:
    ${workExpInfo
      .map(
        (exp) => `
    * ${exp.jobCompanyName}
      ${exp.jobDuration}
      ${exp.jobtitleAndResp}
      ${exp.jobUniqueImpact}
    `,
      )
      .join("\n")}

    Education:
    ${educationInfo
      .map(
        (edu) => `
    * ${edu.degree}
      ${edu.duration}
      ${edu.institute}
      ${edu.major}
    `,
      )
      .join("\n")}
    
    Achievements:
    ${awardsInfo
      .map(
        (award) => `
    * ${award}
    `,
      )
      .join("\n")}
    
    Certifications:
    ${certificationInfo
      .map(
        (certi) => `
    * ${certi}
    `,
      )
      .join("\n")}
    
    languages:
    ${languagesInfo
      .map(
        (language) => `
    * ${language}
    `,
      )
      .join("\n")}

    hobbies:
    ${hobbiesInfo
      .map(
        (hobbie) => `
    * ${hobbie}
    `,
      )
      .join("\n")}
    `;

    const textBlob = new Blob([resumeContent], { type: "text/plain" });
    return textBlob;
  };

  const buildResumeDataWithTextParser = async () => {
    const textBlob = generateBlob();
    const formData = new FormData();
    formData.append("file", textBlob, "ai_generated_resume.txt");

    const response = await axios.post(`/resume/ai/txtresumeformat`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const parser = new ReactiveResumeParser();
    const validatedData = parser.validate(response.data);
    const convertedData = parser.convert(validatedData as ResumeData);

    if (currentTemplate) {
      const defaultColor = "#475569";
      const defaultMargin = 14;
      convertedData.metadata.template = currentTemplate;
      try {
        convertedData.metadata.layout = layoutsList[currentTemplate as keyof typeof layoutsList];
        convertedData.metadata.theme.primary =
          colorList[currentTemplate as keyof typeof colorList] || defaultColor;
        convertedData.metadata.page.margin =
          marginList[currentTemplate as keyof typeof marginList] || defaultMargin;
      } catch {
        // unable to set layout
      }
    }

    return convertedData;
  };

  const buildResumeDataDirectly = () => {
    const intake = createResumeIntakeSnapshot({
      jobDescription,
      jobAnalysis,
      personalInfo,
      workExpInfo,
      selectedSkills,
      educationInfo,
      awardsInfo,
      certificationInfo,
      languagesInfo,
      hobbiesInfo,
      userSummary,
      currentTemplate,
    });

    return buildResumeDataFromIntake(intake);
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    recordLegacyToolStarted("generate_resume");

    try {
      let resumeData: ResumeData;
      let usedDirectResumeData = false;

      if (directAudioResumeGenerationEnabled) {
        try {
          resumeData = buildResumeDataDirectly();
          usedDirectResumeData = true;
        } catch (error) {
          console.error(
            "Direct audio resume generation failed, falling back to text parser:",
            error,
          );
          resumeData = await buildResumeDataWithTextParser();
        }
      } else {
        resumeData = await buildResumeDataWithTextParser();
      }

      try {
        await importResume({ data: resumeData, jobDesc: jobDescription });
      } catch (error) {
        if (
          directAudioResumeGenerationEnabled &&
          usedDirectResumeData &&
          isBadRequestError(error)
        ) {
          console.error(
            "Direct audio resume import was rejected, falling back to text parser:",
            error,
          );
          const fallbackResumeData = await buildResumeDataWithTextParser();
          await importResume({ data: fallbackResumeData, jobDesc: jobDescription });
        } else {
          throw error;
        }
      }

      recordLegacyToolFinished("generate_resume");
      closeDialogue();
    } catch (error) {
      console.error("Error generating resume from audio chat:", error);
      pushMessages({
        type: "ai",
        content: t`I couldn't generate your resume right now. Please try again in a moment.`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddExtras = () => {
    pushMessages({
      type: "user",
      content: t`Add optional details`,
    });
    pushMessages({
      type: "comp",
      comp: "EXTRAS",
    });
    scrollToBottom();
    focusTextarea();
  };

  const handleExtrasDone = () => {
    pushMessages({
      type: "user",
      content: t`Optional details saved`,
    });
    pushMessages({
      type: "ai",
      content: t`Thanks. You can add more details or generate the resume now.`,
      showOptions: true,
    });
    scrollToBottom();
    focusTextarea();
  };
  //---

  useEffect(() => {
    if (step === "GENERATE") {
      const candidateName = personalInfo.name;
      pushMessages({
        type: "ai",
        content: t`Thank you, ${candidateName}. I've gathered all the information needed to build your resume. Is there anything else you'd like to include, or should I proceed with building it ?`,
        showIllustration: true,
        showOptions: true,
      });
      setShowGenerate(true);
      scrollToBottom();
      focusTextarea();
    }
  }, [step]);
  //---

  return showGenerate
    ? messages.map((message, index) => (
        <div key={`gen-${index}`}>
          {message.type !== "comp" && (
            <>
              <div
                className={cn(
                  "mx-1 mb-3 max-w-[80%] rounded-lg px-4 py-2 text-base shadow-md",
                  message.type === "user"
                    ? "ml-auto bg-blue-100 text-blue-800"
                    : "bg-gray-100 text-gray-800",
                )}
              >
                {message.type === "ai" ? (
                  <div className="flex items-center gap-2">
                    <div className="grow">
                      <TypingText message={message.content || ""} />
                    </div>

                    {message.showIllustration && (
                      <div className="size-32 shrink-0">
                        <img
                          alt=""
                          className="float-right h-full scale-125 object-cover object-center"
                          src="/backgrounds/generateChat.svg"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  message.content
                )}
              </div>

              {message.showOptions && messages.length - 1 === index && (
                <div className="my-4 ml-2 flex flex-wrap gap-2">
                  <Button type="button" size="md" className="gap-2" onClick={handleGenerate}>
                    <FilePlus2 size={18} aria-hidden="true" />
                    {t`Generate My Resume`}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="md"
                    className="gap-2"
                    onClick={handleAddExtras}
                  >
                    <Plus size={18} aria-hidden="true" />
                    {t`Add optional details`}
                  </Button>
                </div>
              )}
            </>
          )}

          {message.comp === "EXTRAS" && <ExtrasChat onDone={handleExtrasDone} />}
        </div>
      ))
    : null;
};
